// Zen Journey Components - Shared components for zen-style multi-step forms
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import {
  ALL_COUNTRIES,
  ALL_NICE_CLASSES,
  POPULAR_COUNTRIES,
  POPULAR_CLASSES,
} from "../../utils/trademarkData";

// Swoosh icon SVG path
const SWOOSH_PATH = "M40.577,36.827C36.648,15.416,14.731,0,0,0V1.349c15.7,0,39.9,18.1,39.9,42.152h1.349c0-24.055,24.206-42.152,39.9-42.152V0C66.423,0,44.5,15.416,40.577,36.827";

// Swoosh Icon Component
const SwooshIcon = ({ direction = "right", size = 24 }) => {
  const rotations = {
    left: "rotate(90deg)",
    right: "rotate(-90deg)",
    down: "rotate(180deg)",
    up: "rotate(0deg)",
  };
  return (
    <svg
      width={size}
      height={size * 0.58}
      viewBox="0 0 81.153 43.501"
      style={{ display: "block", transform: rotations[direction] }}
    >
      <path d={SWOOSH_PATH} fill="currentColor" stroke="currentColor" strokeWidth="4" />
    </svg>
  );
};

// Container with gradient background
export const ZenContainer = ({ children }) => (
  <div className="zen-form-container">
    <div className="zen-gradient" />
    <div className="zen-content">{children}</div>
    <style>{zenStyles}</style>
  </div>
);

// Progress dots
export const ZenProgress = ({ steps, currentStep }) => (
  <div className="zen-progress">
    {steps.map((_, i) => (
      <div key={i} className={`zen-dot ${i <= currentStep ? "active" : ""}`} />
    ))}
  </div>
);

// Question/prompt heading
export const ZenQuestion = ({ children }) => (
  <h2 className="zen-question">{children}</h2>
);

// Navigation buttons
export const ZenNav = ({ onBack, onNext, canGoBack, canProceed, loading, isLastStep }) => (
  <div className="zen-nav">
    {canGoBack && (
      <button onClick={onBack} className="zen-nav-btn zen-back" type="button">
        <SwooshIcon direction="left" />
      </button>
    )}
    <div className="zen-nav-spacer" />
    <button
      onClick={onNext}
      disabled={!canProceed || loading}
      className="zen-nav-btn zen-next"
      type="button"
    >
      {loading ? (
        <FontAwesomeIcon icon={faSpinner} spin />
      ) : (
        <SwooshIcon direction={isLastStep ? "down" : "right"} />
      )}
    </button>
  </div>
);

// Text input
export const ZenInput = ({ value, onChange, onKeyDown, placeholder, inputRef }) => (
  <input
    ref={inputRef}
    type="text"
    value={value}
    onChange={e => onChange(e.target.value)}
    onKeyDown={onKeyDown}
    placeholder={placeholder}
    className="zen-input"
  />
);

// Loading state
export const ZenLoading = ({ message = "Loading..." }) => (
  <ZenContainer>
    <div className="zen-loading">
      <FontAwesomeIcon icon={faSpinner} spin className="zen-loading-icon" />
      <p className="zen-loading-text">{message}</p>
    </div>
  </ZenContainer>
);

// Country Tiles with search and expand
export const CountryTiles = ({ selected = [], onChange, showSkip = false, onSkip }) => {
  const [search, setSearch] = useState("");
  const [showAll, setShowAll] = useState(false);

  const filteredCountries = search
    ? ALL_COUNTRIES.filter(c =>
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.code.toLowerCase().includes(search.toLowerCase())
      )
    : showAll
      ? [...POPULAR_COUNTRIES, ...ALL_COUNTRIES.filter(c => !c.popular)]
      : POPULAR_COUNTRIES;

  const toggleCountry = code => {
    const updated = selected.includes(code)
      ? selected.filter(c => c !== code)
      : [...selected, code];
    onChange(updated);
  };

  return (
    <div className="zen-tiles">
      <input
        type="text"
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder="Search countries..."
        className="zen-tiles-search-input"
      />

      <div className="zen-tiles-grid zen-tiles-grid-countries">
        {filteredCountries.map(item => (
          <button
            key={item.code}
            type="button"
            className={`zen-tile ${selected.includes(item.code) ? "selected" : ""}`}
            onClick={() => toggleCountry(item.code)}
            title={item.name}
          >
            <span className="zen-tile-flag">{item.flag}</span>
            <span className="zen-tile-code">{item.code}</span>
          </button>
        ))}
      </div>

      {!search && (
        <button
          type="button"
          className="zen-tiles-toggle"
          onClick={() => setShowAll(!showAll)}
        >
          {showAll ? "− Show less" : `+ ${ALL_COUNTRIES.length - POPULAR_COUNTRIES.length} more`}
        </button>
      )}

      {showSkip && selected.length === 0 && (
        <button type="button" className="zen-skip" onClick={onSkip}>
          Skip this step
        </button>
      )}

      {selected.length > 0 && (
        <div className="zen-selected-chips">
          {selected.map(code => {
            const country = ALL_COUNTRIES.find(c => c.code === code);
            return (
              <button
                key={code}
                type="button"
                className="zen-selected-chip"
                onClick={() => toggleCountry(code)}
              >
                {country?.flag} {code}
                <span className="zen-chip-x">×</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Class Tiles with search and expand
export const ClassTiles = ({ selected = [], onChange, showSkip = false, onSkip }) => {
  const [search, setSearch] = useState("");
  const [showAll, setShowAll] = useState(false);

  const filteredClasses = search
    ? ALL_NICE_CLASSES.filter(c =>
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.code.toString().includes(search)
      )
    : showAll
      ? [...POPULAR_CLASSES, ...ALL_NICE_CLASSES.filter(c => !c.popular)]
      : POPULAR_CLASSES;

  const toggleClass = code => {
    const updated = selected.includes(code)
      ? selected.filter(c => c !== code)
      : [...selected, code];
    onChange(updated);
  };

  return (
    <div className="zen-tiles">
      <input
        type="text"
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder="Search classes..."
        className="zen-tiles-search-input"
      />

      <div className="zen-tiles-grid zen-tiles-grid-classes">
        {filteredClasses.map(item => (
          <button
            key={item.code}
            type="button"
            className={`zen-tile zen-tile-class ${selected.includes(item.code) ? "selected" : ""}`}
            onClick={() => toggleClass(item.code)}
          >
            <span className="zen-tile-class-num">{item.code}</span>
            <span className="zen-tile-class-name">{item.name}</span>
          </button>
        ))}
      </div>

      {!search && (
        <button
          type="button"
          className="zen-tiles-toggle"
          onClick={() => setShowAll(!showAll)}
        >
          {showAll ? "− Show less" : `+ ${ALL_NICE_CLASSES.length - POPULAR_CLASSES.length} more`}
        </button>
      )}

      {showSkip && selected.length === 0 && (
        <button type="button" className="zen-skip" onClick={onSkip}>
          Skip this step
        </button>
      )}

      {selected.length > 0 && (
        <div className="zen-selected-chips">
          {selected.map(code => {
            const cls = ALL_NICE_CLASSES.find(c => c.code === code);
            return (
              <button
                key={code}
                type="button"
                className="zen-selected-chip"
                onClick={() => toggleClass(code)}
              >
                {code}: {cls?.name}
                <span className="zen-chip-x">×</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Shared CSS styles
export const zenStyles = `
  .zen-form-container {
    position: fixed;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(160deg, #f5f0e8 0%, #ebe6de 50%, #f0ebe3 100%);
    overflow: hidden;
  }

  .zen-gradient {
    position: absolute;
    inset: 0;
    background: radial-gradient(ellipse at 50% 30%, rgba(199, 111, 49, 0.08), transparent 60%);
    animation: breathe 10s ease-in-out infinite;
    pointer-events: none;
  }

  @keyframes breathe {
    0%, 100% { opacity: 0.3; transform: scale(1); }
    50% { opacity: 0.6; transform: scale(1.05); }
  }

  .zen-content {
    position: relative;
    z-index: 1;
    width: 100%;
    max-width: 580px;
    padding: 2rem;
    text-align: center;
  }

  .zen-progress {
    display: flex;
    justify-content: center;
    gap: 8px;
    margin-bottom: 4rem;
  }

  .zen-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.15);
    transition: all 0.5s ease;
  }

  .zen-dot.active {
    background: #c76f31;
  }

  .zen-question {
    font-family: 'Arizona_Flare_Thin', Georgia, serif;
    font-size: 1.75rem;
    font-weight: 300;
    color: #333;
    margin: 0 0 3rem 0;
    letter-spacing: -0.02em;
    animation: fadeUp 0.5s ease;
  }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .zen-input-area {
    min-height: 120px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    animation: fadeUp 0.5s ease 0.1s both;
  }

  .zen-input {
    width: 100%;
    background: transparent;
    border: none;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    padding: 1.25rem 0;
    font-family: 'Arizona_Flare_Thin', serif;
    font-size: 1.35rem;
    letter-spacing: 0.01em;
    color: #2a2a2a;
    text-align: center;
    outline: none;
    transition: all 0.4s ease;
    caret-color: #c76f31;
  }

  .zen-input::placeholder {
    color: rgba(0, 0, 0, 0.25);
  }

  .zen-input:focus {
    border-bottom-color: #c76f31;
    border-bottom-width: 2px;
    margin-bottom: -1px;
  }

  .zen-textarea {
    width: 100%;
    background: transparent;
    border: none;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    padding: 1.25rem 0;
    font-family: 'Arizona_Flare_Thin', serif;
    font-size: 1.5rem;
    letter-spacing: 0.01em;
    line-height: 1.5;
    color: #2a2a2a;
    text-align: center;
    outline: none;
    resize: none;
    min-height: 100px;
    transition: all 0.4s ease;
    caret-color: #c76f31;
  }

  .zen-textarea:focus {
    border-bottom-color: #c76f31;
    border-bottom-width: 2px;
    margin-bottom: -1px;
  }

  .zen-textarea::placeholder {
    color: rgba(0, 0, 0, 0.25);
  }

  /* Tile grid styles */
  .zen-tiles {
    animation: fadeUp 0.5s ease 0.1s both;
    width: 100%;
  }

  .zen-tiles-search-input {
    width: 100%;
    padding: 0.5rem 0;
    margin-bottom: 1rem;
    background: transparent;
    border: none;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    font-size: 0.85rem;
    text-align: center;
    outline: none;
    transition: border-color 0.3s ease;
  }

  .zen-tiles-search-input:focus {
    border-bottom-color: #c76f31;
  }

  .zen-tiles-search-input::placeholder {
    color: rgba(0, 0, 0, 0.3);
  }

  .zen-tiles-grid {
    display: grid;
    gap: 8px;
    margin-bottom: 0.75rem;
    max-height: 45vh;
    overflow-y: auto;
    padding: 2px;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  .zen-tiles-grid::-webkit-scrollbar {
    display: none;
  }

  .zen-tiles-grid-countries {
    grid-template-columns: repeat(6, 1fr);
  }

  .zen-tile {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 0.5rem 0.25rem;
    background: transparent;
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .zen-tile:hover {
    border-color: rgba(0, 0, 0, 0.2);
    background: rgba(0, 0, 0, 0.02);
  }

  .zen-tile.selected {
    border-color: #c76f31;
    background: rgba(199, 111, 49, 0.1);
  }

  .zen-tile-flag {
    font-size: 1.5rem;
    line-height: 1;
  }

  .zen-tile-code {
    font-size: 0.6rem;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.4);
    letter-spacing: 0.03em;
    margin-top: 2px;
  }

  .zen-tile.selected .zen-tile-code {
    color: #c76f31;
  }

  .zen-tiles-toggle {
    display: block;
    width: 100%;
    padding: 0.5rem;
    background: transparent;
    border: none;
    color: rgba(0, 0, 0, 0.35);
    font-size: 0.7rem;
    cursor: pointer;
    transition: color 0.2s ease;
  }

  .zen-tiles-toggle:hover {
    color: #c76f31;
  }

  .zen-skip {
    display: block;
    width: 100%;
    padding: 0.75rem;
    margin-top: 0.5rem;
    background: transparent;
    border: none;
    color: rgba(0, 0, 0, 0.3);
    font-size: 0.75rem;
    cursor: pointer;
    transition: color 0.2s ease;
  }

  .zen-skip:hover {
    color: #c76f31;
  }

  .zen-selected-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    justify-content: center;
  }

  .zen-selected-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 0.35rem 0.6rem;
    background: rgba(199, 111, 49, 0.1);
    border: 1px solid rgba(199, 111, 49, 0.3);
    border-radius: 100px;
    font-size: 0.7rem;
    color: #c76f31;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .zen-selected-chip:hover {
    background: rgba(199, 111, 49, 0.2);
    border-color: #c76f31;
  }

  .zen-chip-x {
    font-size: 0.85rem;
    line-height: 1;
    opacity: 0.6;
  }

  .zen-selected-chip:hover .zen-chip-x {
    opacity: 1;
  }

  /* Nice Classes grid */
  .zen-tiles-grid-classes {
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
  }

  .zen-tile-class {
    flex-direction: row;
    gap: 8px;
    padding: 0.6rem 0.75rem;
    justify-content: flex-start;
  }

  .zen-tile-class-num {
    font-family: 'GT-Pressura-Mono', 'SF Mono', monospace;
    font-size: 0.9rem;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.25);
    min-width: 22px;
  }

  .zen-tile-class.selected .zen-tile-class-num {
    color: #c76f31;
  }

  .zen-tile-class-name {
    font-size: 0.65rem;
    color: rgba(0, 0, 0, 0.5);
    text-align: left;
    line-height: 1.2;
  }

  .zen-tile-class.selected .zen-tile-class-name {
    color: rgba(0, 0, 0, 0.7);
  }

  /* Navigation */
  .zen-nav {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 4rem;
    gap: 1rem;
    animation: fadeUp 0.5s ease 0.2s both;
  }

  .zen-nav-spacer {
    flex: 1;
  }

  .zen-nav-btn {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    border: none;
    background: transparent;
    color: rgba(0, 0, 0, 0.3);
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
    outline: none;
    box-shadow: none;
  }

  .zen-nav-btn:hover:not(:disabled) {
    color: #c76f31;
    background: rgba(199, 111, 49, 0.1);
  }

  .zen-nav-btn:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  .zen-back {
    background: transparent;
  }

  .zen-next {
    background: rgba(199, 111, 49, 0.1);
    color: #c76f31;
  }

  .zen-next:hover:not(:disabled) {
    background: #c76f31;
    color: white;
  }

  /* Loading state */
  .zen-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 200px;
    animation: fadeUp 0.3s ease;
  }

  .zen-loading-icon {
    font-size: 2rem;
    color: #c76f31;
    margin-bottom: 1rem;
  }

  .zen-loading-text {
    font-family: 'Arizona_Flare_Thin', Georgia, serif;
    font-size: 1.1rem;
    color: rgba(0, 0, 0, 0.4);
    margin: 0;
  }

  /* Success state */
  .zen-success {
    animation: fadeUp 0.5s ease;
  }

  .zen-success-icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: rgba(199, 111, 49, 0.1);
    color: #c76f31;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    margin: 0 auto 1.5rem;
  }

  .zen-success-text {
    font-family: 'Arizona_Flare_Thin', Georgia, serif;
    font-size: 1.5rem;
    color: #333;
    margin: 0;
  }

  /* Error */
  .zen-error {
    text-align: center;
    color: #c76f31;
    font-size: 0.85rem;
    padding: 0.75rem 1rem;
    background: rgba(199, 111, 49, 0.08);
    border-radius: 6px;
    margin-top: 1rem;
    animation: fadeUp 0.3s ease;
  }

  /* Review screen */
  .zen-review {
    animation: fadeUp 0.5s ease 0.1s both;
    width: 100%;
    text-align: left;
  }

  .zen-review-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  .zen-review-item {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .zen-review-item-full {
    grid-column: 1 / -1;
    text-align: center;
  }

  .zen-review-label {
    font-size: 0.6rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: rgba(0, 0, 0, 0.35);
  }

  .zen-review-value {
    font-size: 0.9rem;
    color: rgba(0, 0, 0, 0.7);
  }

  .zen-review-value-name {
    font-family: 'Oswald', sans-serif;
    font-weight: 600;
    text-transform: uppercase;
    font-size: 1.5rem;
    color: #555;
    letter-spacing: -0.01em;
  }

  .zen-review-block {
    margin-bottom: 1.25rem;
    padding-top: 1rem;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
  }

  .zen-review-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 0.5rem;
  }

  .zen-review-tag {
    display: inline-block;
    padding: 0.3rem 0.6rem;
    background: rgba(0, 0, 0, 0.04);
    border-radius: 4px;
    font-size: 0.7rem;
    color: rgba(0, 0, 0, 0.6);
  }

  /* History styles */
  .zen-history {
    width: 100%;
    margin-top: 2.5rem;
    animation: fadeUp 0.5s ease 0.2s both;
  }

  .zen-history-toggle {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    padding: 0.5rem;
    background: transparent;
    border: none;
    color: rgba(0, 0, 0, 0.3);
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    cursor: pointer;
    transition: color 0.2s ease;
  }

  .zen-history-toggle:hover {
    color: #c76f31;
  }

  .zen-history-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 10px;
    margin-top: 1rem;
    max-height: 280px;
    overflow-y: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  .zen-history-grid::-webkit-scrollbar {
    display: none;
  }

  .zen-history-tile {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 90px;
    padding: 1rem 0.75rem 0.6rem;
    background: rgba(255, 255, 255, 0.6);
    border: 1px solid rgba(0, 0, 0, 0.06);
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.15s ease;
    text-align: center;
  }

  .zen-history-tile:hover {
    border-color: rgba(199, 111, 49, 0.3);
    background: rgba(255, 255, 255, 0.9);
  }

  .zen-history-tile-grade {
    position: absolute;
    top: 8px;
    right: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    font-size: 0.7rem;
    font-weight: 700;
    border: 2px solid;
  }

  .zen-history-tile-name {
    font-family: 'Oswald', sans-serif;
    font-weight: 600;
    text-transform: uppercase;
    font-size: 1rem;
    color: #555;
    letter-spacing: -0.01em;
    transform: skewX(-6deg);
    margin-bottom: 0.4rem;
  }

  .zen-history-tile-meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 6px;
    font-size: 0.55rem;
    color: rgba(0, 0, 0, 0.4);
  }

  .zen-history-tile-countries {
    padding: 2px 5px;
    background: rgba(0, 0, 0, 0.04);
    border-radius: 3px;
  }

  .zen-history-tile-classes {
    color: rgba(0, 0, 0, 0.35);
  }

  .zen-history-tile-date {
    position: absolute;
    bottom: 6px;
    right: 8px;
    font-size: 0.5rem;
    color: rgba(0, 0, 0, 0.25);
    text-transform: uppercase;
    letter-spacing: 0.02em;
  }

  /* Search options */
  .zen-search-options {
    margin-top: 1.5rem;
    padding-top: 1rem;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
  }

  .zen-search-options-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    margin-top: 0.75rem;
  }

  .zen-search-option {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    padding: 0.75rem 0.5rem;
    background: transparent;
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .zen-search-option:hover {
    border-color: rgba(0, 0, 0, 0.2);
    background: rgba(0, 0, 0, 0.02);
  }

  .zen-search-option.selected {
    border-color: #c76f31;
    background: rgba(199, 111, 49, 0.1);
  }

  .zen-search-option-label {
    font-size: 0.8rem;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.7);
  }

  .zen-search-option.selected .zen-search-option-label {
    color: #c76f31;
  }

  .zen-search-option-desc {
    font-size: 0.6rem;
    color: rgba(0, 0, 0, 0.4);
  }

  /* Navigation swoosh icons */
  .zen-swoosh-icon {
    display: block;
  }

  .zen-swoosh-left {
    transform: rotate(90deg);
  }

  .zen-swoosh-right {
    transform: rotate(-90deg);
  }

  .zen-swoosh-down {
    transform: rotate(180deg);
  }

  /* Results view */
  .zen-results-content {
    position: relative;
    z-index: 1;
    width: 100%;
    max-width: 900px;
    max-height: 100vh;
    overflow-y: auto;
    padding: 2rem;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  .zen-results-content::-webkit-scrollbar {
    display: none;
  }

  .zen-back-to-search {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 0.5rem 1rem;
    background: rgba(255, 255, 255, 0.6);
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 100px;
    font-size: 0.75rem;
    color: rgba(0, 0, 0, 0.4);
    cursor: pointer;
    transition: all 0.2s ease;
    margin-bottom: 2rem;
  }

  .zen-back-to-search:hover {
    background: rgba(255, 255, 255, 0.9);
    border-color: #c76f31;
    color: #c76f31;
  }

  /* Hero section */
  .zen-results-hero {
    text-align: center;
    margin-bottom: 2rem;
  }

  .zen-results-grade {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 72px;
    height: 72px;
    border-radius: 50%;
    font-size: 2rem;
    font-weight: 700;
    border: 3px solid;
    margin-bottom: 1rem;
  }

  .zen-results-name {
    font-family: 'Oswald', sans-serif;
    font-weight: 600;
    text-transform: uppercase;
    font-size: 2.5rem;
    color: #555;
    letter-spacing: -0.02em;
    margin: 0 0 0.5rem 0;
    transform: skewX(-6deg);
  }

  .zen-results-link {
    font-size: 0.75rem;
    color: rgba(0, 0, 0, 0.35);
    text-decoration: none;
    transition: color 0.2s ease;
  }

  .zen-results-link:hover {
    color: #c76f31;
  }

  /* Stats row */
  .zen-results-stats {
    display: flex;
    justify-content: center;
    gap: 3rem;
    padding: 1.5rem 0;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    margin-bottom: 2rem;
  }

  .zen-stat {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.25rem;
  }

  .zen-stat-value {
    font-family: 'GT-Pressura-Mono', 'SF Mono', monospace;
    font-size: 1.5rem;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.7);
  }

  .zen-stat-label {
    font-size: 0.65rem;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: rgba(0, 0, 0, 0.35);
  }

  /* Conflicts section */
  .zen-conflicts {
    animation: fadeUp 0.5s ease;
  }

  .zen-conflicts-title {
    font-size: 0.7rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: rgba(0, 0, 0, 0.35);
    margin: 0 0 1rem 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .zen-conflicts-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    background: rgba(0, 0, 0, 0.06);
    border-radius: 10px;
    font-size: 0.65rem;
    color: rgba(0, 0, 0, 0.5);
  }

  .zen-conflicts-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 12px;
  }

  .zen-conflict-tile {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 100px;
    padding: 1rem 0.75rem;
    background: rgba(255, 255, 255, 0.6);
    border: 1px solid;
    border-radius: 12px;
    text-decoration: none;
    transition: all 0.15s ease;
  }

  .zen-conflict-tile:hover {
    background: rgba(255, 255, 255, 0.9);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }

  .zen-conflict-sim {
    position: absolute;
    top: 8px;
    right: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 36px;
    height: 24px;
    padding: 0 6px;
    border-radius: 12px;
    font-size: 0.7rem;
    font-weight: 600;
  }

  .zen-conflict-name {
    font-family: 'Oswald', sans-serif;
    font-weight: 500;
    text-transform: uppercase;
    font-size: 0.95rem;
    color: #555;
    letter-spacing: -0.01em;
    transform: skewX(-6deg);
    text-align: center;
    margin-bottom: 0.5rem;
  }

  .zen-conflict-meta {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: 0.6rem;
    color: rgba(0, 0, 0, 0.4);
  }

  .zen-conflict-country {
    padding: 2px 6px;
    background: rgba(0, 0, 0, 0.04);
    border-radius: 4px;
    font-weight: 500;
  }

  .zen-conflict-classes {
    color: rgba(0, 0, 0, 0.35);
  }

  .zen-no-conflicts {
    text-align: center;
    padding: 3rem;
    color: rgba(0, 0, 0, 0.3);
    font-size: 0.9rem;
  }

  /* Decision flow */
  .zen-decision-flow {
    background: rgba(255, 255, 255, 0.5);
    border-radius: 12px;
    padding: 1.5rem;
    margin-bottom: 2rem;
  }
`;

const ZenComponents = {
  ZenContainer,
  ZenProgress,
  ZenQuestion,
  ZenNav,
  ZenInput,
  ZenLoading,
  CountryTiles,
  ClassTiles,
  zenStyles,
};

export default ZenComponents;
